import { useSearch } from '@tanstack/react-router'
import { useState } from 'react'

import {
  Button,
  CardContent,
  CardFooter,
  CardHeader,
  Link,
  TextField,
} from '@/components'
import { useForm } from '@/stores'
import { useAuthStateChange } from '../hooks'

interface AuthFormProps {
  title: string
  description?: string
  footerTitle: string
  footerLabel: string
  footerLink: { to: string; label: string }
  onSubmit: (email: string) => Promise<void>
  onSignedIn: () => void
}

export const AuthForm = ({
  title,
  description,
  footerLabel,
  footerTitle,
  footerLink,
  onSubmit,
  onSignedIn,
}: AuthFormProps) => {
  const search = useSearch({ strict: false })
  const [isLoading, setIsLoading] = useState(false)
  const { data, errors, setFieldValue, setFieldError } = useForm()

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    onSubmit((data?.email as string) || '')
      .catch((error) => setFieldError('email', error))
      .finally(() => setIsLoading(false))
  }

  useAuthStateChange({ onSignedIn })

  return (
    <>
      <CardHeader className="space-y-1 pb-4">
        <h1 className="text-2xl font-semibold tracking-tight text-center">
          {title}
        </h1>
        {description && (
          <p className="text-sm text-muted-foreground text-center">
            {description}
          </p>
        )}
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <TextField
            label="Email address"
            helpText="You will receive a 6-digit code to confirm your email address."
            type="email"
            placeholder="name@example.com"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue('email', e.target.value)
            }
            required
            autoComplete="email"
            autoFocus
            error={errors?.email}
          />

          <Button
            type="submit"
            className="w-full h-10 font-medium"
            isLoading={isLoading}
            loadingText="Sending code..."
          >
            Continue with email
          </Button>
        </form>
      </CardContent>

      <CardFooter className="flex flex-col space-y-4 pt-2">
        <div className="relative w-full">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-muted-foreground">
              {footerTitle}
            </span>
          </div>
        </div>

        <div className="text-center text-sm">
          <span className="text-muted-foreground">{footerLabel} </span>
          <Link to={footerLink.to} search={search} className="font-medium">
            {footerLink.label}
          </Link>
        </div>
      </CardFooter>
    </>
  )
}
